import { useState } from 'react';
import type { MailcomAccountsResponse } from '../../api/endpoints.js';
import type { useApiQuery } from '../../api/hooks.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
} from '../../ui/index.js';
import { ParentMailsTable } from './ParentMailsTable.js';

// R-D214 — V1-parity status taxonomy. Status-bucket tokens map to the
// 5-bucket DB enum; taxonomy tokens (alias_limit_reached / renewal_failed /
// renewal_in_progress / upstream_unavailable / blocked / bootstrapping)
// land on status_detail or suspended_reason inside the client (see
// listMailcomAccounts in endpoints.ts).
type StatusFilter =
  | 'all'
  | 'active'
  | 'draining'
  | 'suspended'
  | 'login_failed'
  | 'disabled'
  | 'alias_limit_reached'
  | 'renewal_failed'
  | 'renewal_in_progress'
  | 'upstream_unavailable'
  | 'blocked'
  | 'bootstrapping';

interface AccountsGridProps {
  readonly providerId: string;
  readonly query: ReturnType<typeof useApiQuery<MailcomAccountsResponse>>;
  readonly canManage: boolean;
  readonly onOpenAliases: (accountId: string) => void;
  readonly onOpenRenewals: (accountId: string) => void;
  readonly onOpenCredential: (accountId: string) => void;
  readonly onAfterAction: () => void;
  readonly page: number;
  readonly onPageChange: (n: number) => void;
  readonly search: string;
  readonly onSearchChange: (s: string) => void;
  readonly statusFilter: StatusFilter;
  readonly onStatusFilterChange: (s: StatusFilter) => void;
  readonly pageSize: number;
}

export function AccountsGrid({
  providerId,
  query,
  canManage,
  onOpenAliases,
  onOpenRenewals,
  onOpenCredential,
  onAfterAction,
  page,
  onPageChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  pageSize,
}: AccountsGridProps): JSX.Element {
  const [searchDraft, setSearchDraft] = useState(search);
  const accounts = query.kind === 'ok' ? query.data?.accounts ?? [] : [];
  const pagination = query.kind === 'ok' ? query.data?.pagination : undefined;

  return (
    <Card flush data-testid="mailcom-accounts-section">
      <CardHeader
        title="Parent Mail Listesi"
        actions={
          pagination ? (
            <span className="t-tertiary t-small">
              Toplam: <strong style={{ color: 'var(--text-primary)' }}>{pagination.total.toLocaleString('tr-TR')}</strong>{' '}
              · sayfa {pagination.page} / {pagination.pages}
            </span>
          ) : null
        }
      />
      <CardBody>
        <form
          style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}
          data-testid="mailcom-account-filters"
          onSubmit={(ev) => {
            ev.preventDefault();
            onSearchChange(searchDraft.trim());
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <Input
              type="search"
              placeholder="E-posta / handle ara…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              aria-label="parent e-posta ara"
              data-testid="filter-email"
              iconLeft={<Icon.Search size={14} />}
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
            aria-label="durum filtresi"
            data-testid="filter-status"
            style={{ width: 220 }}
          >
            <option value="all">Tüm durumlar</option>
            {/* Status buckets (DB enum) */}
            <option value="active">Aktif</option>
            <option value="draining">Draining</option>
            <option value="suspended">Askıda</option>
            <option value="login_failed">Giriş Hatası</option>
            <option value="disabled">Devre Dışı</option>
            {/* R-D214 V1-taxonomy tokens (client routes onto statusDetail /
                suspendedReason inside listMailcomAccounts). */}
            <option value="renewal_in_progress">Yenileniyor</option>
            <option value="renewal_failed">Yenileme Hatası</option>
            <option value="alias_limit_reached">Alias Limiti (Retry)</option>
            <option value="upstream_unavailable">Upstream Erişilemez</option>
            <option value="blocked">Engellendi</option>
            <option value="bootstrapping">Bootstrapping</option>
          </Select>
          <Button type="submit" variant="primary" icon={<Icon.Search size={14} />}>Ara</Button>
        </form>

        {query.kind === 'loading' ? <LoadingState /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onAfterAction} /> : null}
        {query.kind === 'ok' ? (
          accounts.length === 0 ? (
            <EmptyState
              label={
                search.length > 0 || statusFilter !== 'all'
                  ? 'Filtrelerle eşleşen parent mail bulunamadı.'
                  : 'Henüz parent mail hesabı yok.'
              }
              hint={
                search.length > 0 || statusFilter !== 'all'
                  ? 'E-posta arama veya durum filtresini değiştirip tekrar deneyin.'
                  : 'Üstteki "Yeni Parent" veya "Import / TXT" butonuyla mail.com hesabı kaydedebilirsiniz.'
              }
              icon={<Icon.Mail size={22} />}
            />
          ) : (
            <ParentMailsTable
              providerId={providerId}
              accounts={accounts}
              canManage={canManage}
              onOpenAliases={onOpenAliases}
              onOpenRenewals={onOpenRenewals}
              onOpenCredential={onOpenCredential}
              onAfterAction={onAfterAction}
            />
          )
        ) : null}

        {pagination && pagination.pages > 1 ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid var(--border-subtle)',
            }}
            data-testid="mailcom-accounts-pagination"
          >
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              icon={<Icon.ChevronLeft size={14} />}
            >
              Önceki
            </Button>
            <span className="t-tertiary t-small">
              Sayfa {pagination.page} / {pagination.pages} · sayfa başına {pageSize}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= pagination.pages}
              onClick={() => onPageChange(page + 1)}
              iconRight={<Icon.ChevronRight size={14} />}
            >
              Sonraki
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
